"use client";
import { ChevronRight, Calendar, Minus, Plus, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import StepMarker from "../StepMarker";
import { Button } from "../Button";
import { LocationInput } from "../../location-picker";
import { fetchMasterCities } from "../../../services/locationServices";

export default function Step2EventDetails({
  formData,
  updateFormData,
  onBack,
  onNext,
  onShowLocationSearch,
  onShowEventTypeModal
}) {
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    const loadCities = async () => {
      try {
        setLoadingCities(true);
        const response = await fetchMasterCities();
        if (response && response.data) {
          setCities(response.data);
          updateFormData({ city: response.data[0].city });
        }
      } catch (error) {
        console.error("Failed to load cities:", error);
      } finally {
        setLoadingCities(false);
      }
    };

    loadCities();
  }, []);

  const handleChange = e => {
    updateFormData({ [e.target.name]: e.target.value });
  };

  const handleGuestsChange = amount => {
    updateFormData({ guests: Math.max(1, formData.guests + amount) });
  };

  const handleBudgetChange = amount => {
    updateFormData({ budget: Math.max(100, formData.budget + amount) });
  };

  return (
    <div>
      <StepMarker
        currentStep={2}
        totalSteps={formData.eventType === "Community Events" ? 3 : 5}
        onBack={onBack}
      />

      <h2 className="text-xl font-bold mb-1">Tell us about your event</h2>
      <p className="text-gray-500 mb-6">Make it truly memorable</p>

      <div className="space-y-4">
        <div>
          <div className="relative">
            <input
              id="eventName"
              name="eventName"
              value={formData.eventName}
              onChange={handleChange}
              placeholder="Event Name*"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
              required
            />
          </div>
        </div>

        <div>
          {formData.eventType == "Personal Events" && (
            <div className="relative">
              <input
                id="eventTypeDetail"
                name="eventTypeDetail"
                value={formData.eventTypeDetail || ""}
                placeholder="Select Event Type*"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent pr-10 cursor-pointer"
                required
                readOnly
                onClick={() => onShowEventTypeModal(formData.eventTypeDetail)}
              />
              <ChevronRight
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
            </div>
          )}
        </div>

        <div>
          <div className="relative">
            <input
              id="date"
              name="date"
              type="text"
              value={formData.date}
              onChange={handleChange}
              placeholder="Select Date*"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent pr-10"
              required
              onFocus={e => (e.target.type = "date")}
              onBlur={e => (e.target.type = formData.date ? "text" : "date")}
            />
            <Calendar
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
          </div>
        </div>
        {formData.eventType != "Personal Events" && (
          <div className="relative mt-4 ">
            <div className="uppercase text-xs font-medium text-gray-500 mb-2">
              Start Time*
            </div>

            <input
              id="time"
              name="startTime"
              type="time"
              value={formData.startTime}
              placeholder="Start Time*"
              className="w-full px-4 py-3 mb-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent pr-10 cursor-pointer"
              required
              onChange={e => updateFormData({ startTime: e.target.value })}
            />
            <div className="uppercase text-xs font-medium text-gray-500 mb-2">
              End Time*
            </div>
            <input
              id="time"
              name="endTime"
              type="time"
              value={formData.endTime}
              placeholder="End Time*"
              className="w-full px-4 py-3  border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent pr-10 cursor-pointer"
              required
              onChange={e => updateFormData({ endTime: e.target.value })}
            />
            <div className="relative">
              <select
                id="city"
                name="city"
                value={formData.city}
                className="w-full mt-4 mb-4 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent pr-10 cursor-pointer appearance-none bg-white"
                required
                onChange={e => updateFormData({ city: e.target.value })}
                disabled={loadingCities}
              >
                <option value="" disabled>
                  {loadingCities ? "Loading cities..." : "Select City*"}
                </option>
                {cities.map(cityData => (
                  <option key={cityData.id} value={cityData.city}>
                    {cityData.city}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                size={20}
              />
            </div>
            <input
              id="state"
              name="state"
              type="text"
              value={formData.state}
              placeholder="State*"
              className="w-full px-4 py-3  border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent pr-10 cursor-pointer"
              required
              onChange={e => updateFormData({ state: e.target.value })}
            />
          </div>
        )}

        <div>
          {/*
          <div className="relative">
            <input
              id="venue"
              name="venue"
              value={formData.venue}
              placeholder="Venue*"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent pr-10 cursor-pointer"
              required
              readOnly
              onClick={() => onShowLocationSearch(formData.venue)}
            />
            <ChevronRight
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
          </div>
          */}
          <LocationInput
            value={formData.venue}
            onChange={location => {
              updateFormData({ venue: location });
            }}
          />
        </div>

        {formData.eventType === "Personal Events" && (
          <div>
            <div className="uppercase text-xs font-medium text-gray-500 mb-2">
              EXPECTED NUMBER OF GUESTS*
            </div>
            <div className="flex items-center">
              <div className="flex-1 relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-gray-400"
                  >
                    <path
                      d="M10 10.8333C11.3807 10.8333 12.5 9.71404 12.5 8.33333C12.5 6.95262 11.3807 5.83333 10 5.83333C8.61929 5.83333 7.5 6.95262 7.5 8.33333C7.5 9.71404 8.61929 10.8333 10 10.8333Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15.8333 8.33333C15.8333 12.9357 10 16.6667 10 16.6667C10 16.6667 4.16667 12.9357 4.16667 8.33333C4.16667 6.56522 4.87053 4.86953 6.12087 3.61919C7.37121 2.36885 9.0669 1.66667 10.8333 1.66667C12.6014 1.66667 14.2971 2.36885 15.5474 3.61919C16.7978 4.86953 17.5 6.56522 17.5 8.33333V8.33333Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <input
                  type="number"
                  name="guests"
                  value={formData.guests}
                  onChange={e =>
                    updateFormData({
                      guests: Number.parseInt(e.target.value) || 1
                    })
                  }
                  className="w-full h-12 pl-12 pr-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                  min="1"
                />
              </div>
              <button
                type="button"
                onClick={() => handleGuestsChange(-1)}
                className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg ml-3 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <Minus size={16} />
              </button>
              <button
                type="button"
                onClick={() => handleGuestsChange(1)}
                className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg ml-3 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
        )}

        {formData.eventType === "Personal Events" && (
          <div>
            <div className="uppercase text-xs font-medium text-gray-500 mb-2">
              PLANNED BUDGET*
            </div>
            <div className="flex items-center">
              <div className="flex-1 relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-gray-400"
                  >
                    <path
                      d="M10 18.3333C14.6024 18.3333 18.3334 14.6024 18.3334 10C18.3334 5.39763 14.6024 1.66667 10 1.66667C5.39765 1.66667 1.66669 5.39763 1.66669 10C1.66669 14.6024 5.39765 18.3333 10 18.3333Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 6.66667V10"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7.5 10H12.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <input
                  type="number"
                  name="budget"
                  value={formData.budget}
                  onChange={e =>
                    updateFormData({
                      budget: Number.parseInt(e.target.value) || 100
                    })
                  }
                  className="w-full h-12 pl-12 pr-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                  min="100"
                  step="50"
                />
              </div>
              <button
                type="button"
                onClick={() => handleBudgetChange(-50)}
                className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg ml-3 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <Minus size={16} />
              </button>
              <button
                type="button"
                onClick={() => handleBudgetChange(50)}
                className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg ml-3 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
        )}

        <div>
          <div className="uppercase text-xs font-medium text-gray-500 mb-2">
            MORE DESCRIPTION*
          </div>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Add a personal touch—share your event vibe to excite attendees!"
            rows={4}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
          />
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={onBack} className="px-6">
          Back
        </Button>
        <Button
          onClick={onNext}
          className="bg-[#7B189F] hover:bg-[#671485] text-white px-6"
          disabled={
            !formData.eventName ||
            !formData.date ||
            (formData.eventType === "Personal Events" &&
              !formData.eventTypeDetail) ||
            (formData.eventType !== "Personal Events" &&
              (!formData.startTime || !formData.endTime || !formData.state))
          }
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
